import DayjsUtils from '@date-io/dayjs';
import { Button, Grid, TextField, Typography } from '@material-ui/core';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import ptBR from 'dayjs/locale/pt-br';
import ButtonWithLoading from 'components/atoms/ButtonWithLoading';
import BaseModal from 'components/molecules/AnomalyGrid/components/BaseModal';
import { IModalFinish } from 'components/molecules/AnomalyGrid/components/ModalFinish/type';
import React from 'react';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import Dropzone from 'components/atoms/Dropzone';
import { postAnomalyFinish, postUploadFiles } from 'services/anomaly';
import * as Yup from 'yup';
import useStyles from './style';

const FORM_ID = 'ANOMALY_FORM_MODAL_FINISH';

const DEFAULT_HELPER_TEXT = 'Obrigatório';
const DEFAULT_ERROR_TEXT = 'Preenchimento inválido';
const ERROR_TEXT = 'Texto ultrapassou o limite de 1000 caracteres';
const MAX_DATE = dayjs(dayjs().format('YYYY-MM-DD'));

const ModalFinish = ({
  open,
  onClose,
  children,
  gridRow,
  reloadData,
}: IModalFinish) => {
  const classes = useStyles();
  const [loadingSaveForm, setLoadingSaveForm] = React.useState<boolean>(false);
  const [files, setFiles] = React.useState<File[]>([]);

  const minDate = gridRow?.createdDate;
  const validationSchema = Yup.object({
    conclusion: Yup.mixed()
      .required(DEFAULT_HELPER_TEXT)
      .nullable()
      .test('is-greater-than', DEFAULT_ERROR_TEXT, value => {
        const test = dayjs(value);

        return (
          dayjs(value).isValid() &&
          (test.isSame(minDate) ||
            (test.isAfter(minDate) && test.isBefore(dayjs())))
        );
      })
      .typeError(DEFAULT_ERROR_TEXT),
    comment: Yup.string().max(1000, ERROR_TEXT).required(DEFAULT_HELPER_TEXT),
  });

  const formik = useFormik({
    initialValues: {
      conclusion: null,
      comment: '',
    },
    validationSchema,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onSubmit: () => {},
  });

  const formDate = React.useMemo(() => {
    const date = gridRow?.createdDate;
    return dayjs(date);
  }, [gridRow?.createdDate]);

  const handleDateChange = React.useCallback(
    (name, value) => {
      formik.setFieldTouched(name);
      formik.setFieldValue(name, value);
    },
    [formik],
  );

  const sendFile = React.useCallback(() => {
    const uploadFiles = files.map(file => {
      const params = {
        path: file.name,
        file,
      };
      // params.append('file', file);
      return new Promise<{ image?: string }>((resolve, reject) => {
        postUploadFiles(params)
          .then(url => {
            let response = {};
            if (file.type.includes('image')) {
              response = { image: url };
            }
            resolve(response);
          })
          .catch(e => reject(e));
      });
    });

    const dataPromise = new Promise((resolve, reject) => {
      Promise.all(uploadFiles)
        .then(uploadData => {
          const anomalyId = gridRow?.id;
          const photoUrl = uploadData.map(upload => upload.image);
          let conclusionPhoto;

          if (photoUrl && photoUrl.length >= 0) [conclusionPhoto] = photoUrl;

          const data = {
            ...formik.values,
            conclusionPhoto,
            anomalyId,
          };

          postAnomalyFinish(data)
            .then(e => resolve(e))
            .catch(e => reject(e));
        })
        .catch(e => resolve(e));
    });

    setLoadingSaveForm(true);
    dataPromise
      .then(() => {
        if (reloadData) reloadData();
        onClose();
      })
      .finally(() => setLoadingSaveForm(false));
  }, [files, formik.values, gridRow?.id, onClose, reloadData]);

  React.useEffect(() => {
    if (!open) {
      if (formik.dirty || !formik.isValid) {
        formik.resetForm();
      }

      if (files.length > 0) setFiles([]);
    }
  }, [files.length, formik, open]);

  const renderBody = React.useCallback(() => {
    return (
      <form id={FORM_ID} onSubmit={formik.handleSubmit}>
        <Typography className={classes.formMessage}>
          Preencha os dados obrigatórios para concluir a anomalia:
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <KeyboardDatePicker
              id="creation"
              name="creation"
              label="Data de criação"
              inputVariant="outlined"
              format="DD/MM/YY"
              onChange={date => handleDateChange('creation', date)}
              value={formDate}
              disabled
              className={classes.inputList}
            />
          </Grid>
          <Grid item xs={6}>
            <KeyboardDatePicker
              id="conclusion"
              name="conclusion"
              label="Data de conclusão"
              inputVariant="outlined"
              format="DD/MM/YY"
              cancelLabel="Cancelar"
              clearLabel="Limpar"
              minDate={formDate}
              maxDate={MAX_DATE}
              KeyboardButtonProps={{
                'aria-label': 'change date',
              }}
              onChange={date => handleDateChange('conclusion', date)}
              value={formik.values.conclusion}
              onBlur={formik.handleBlur}
              error={
                formik.touched.conclusion && Boolean(formik.errors.conclusion)
              }
              helperText={
                (formik.touched.conclusion && formik.errors.conclusion) ||
                DEFAULT_HELPER_TEXT
              }
              clearable
              className={classes.inputList}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              id="comment"
              name="comment"
              label="Comentário"
              variant="outlined"
              onChange={formik.handleChange}
              value={formik.values.comment}
              onBlur={formik.handleBlur}
              error={formik.touched.comment && Boolean(formik.errors.comment)}
              helperText={
                (formik.touched.comment && formik.errors.comment) ||
                DEFAULT_HELPER_TEXT
              }
            />
          </Grid>
          <Grid item xs={12} className={classes.content}>
            <Typography className={classes.formMessage}>
              Caso queira anexar uma imagem comprovando a solução:
            </Typography>
            <Dropzone
              accept="image/png,image/jpeg,image/jpg"
              maxFiles={1}
              files={files}
              setFiles={setFiles}
              maxResolution={1600}
              maxFilesByType={{ image: 1 }}
            />
          </Grid>
        </Grid>
      </form>
    );
  }, [
    classes.content,
    classes.formMessage,
    classes.inputList,
    files,
    formDate,
    formik.errors.comment,
    formik.errors.conclusion,
    formik.handleBlur,
    formik.handleChange,
    formik.handleSubmit,
    formik.touched.comment,
    formik.touched.conclusion,
    formik.values.comment,
    formik.values.conclusion,
    handleDateChange,
  ]);

  const renderFooter = React.useCallback(() => {
    return (
      <div className={classes.footer}>
        <Button color="primary" onClick={onClose}>
          Cancelar
        </Button>

        <ButtonWithLoading
          disabled={!formik.dirty || !formik.isValid}
          type="submit"
          color="primary"
          isLoading={loadingSaveForm}
          form={FORM_ID}
          onClick={sendFile}
        >
          Concluir
        </ButtonWithLoading>
      </div>
    );
  }, [
    classes.footer,
    formik.dirty,
    formik.isValid,
    loadingSaveForm,
    onClose,
    sendFile,
  ]);

  return (
    <MuiPickersUtilsProvider locale={ptBR} utils={DayjsUtils}>
      <BaseModal
        title="Concluir"
        body={renderBody()}
        footer={renderFooter()}
        open={open}
        onClose={onClose}
        width="629px"
      >
        {children}
      </BaseModal>
    </MuiPickersUtilsProvider>
  );
};

export default ModalFinish;
